import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { formatShortDate, formatShortDateTime } from '../../utils/dateUtils';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import ErrorDisplay from '../error-display/error-display';
import useFirestoreQuery from '../../hooks/useFirestoreQuery';
import 'bootstrap-icons/font/bootstrap-icons.css';

const BlogList = () => {
  const [error, setError] = useState('');
  const { isAdmin } = useAuth();
  
  // Use our custom hook to fetch all blogs
  const { data: blogs = [], loading, error: fetchError } = useFirestoreQuery({
    queryFn: () => query(collection(db, 'blogs'), orderBy('createdAt', 'desc')),
    dependencies: [],
    transformFn: (querySnapshot) => {
      const blogsList = [];
      querySnapshot.forEach((doc) => {
        blogsList.push({
          ...doc.data(),
          id: doc.id,
        });
      });
      return blogsList;
    }
  });
  
  // Combine fetch error with any other errors
  const combinedError = fetchError || error;

  const handleDelete = async (blogId) => {
    if (!isAdmin) {
      setError('You do not have permission to delete blogs');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'blogs', blogId));
        // No need to update state manually as the query will re-run
      } catch (err) {
        setError('Error deleting blog: ' + err.message);
      }
    }
  };

  const handleTogglePublish = async (blog) => {
    if (!isAdmin) {
      setError('You do not have permission to publish/unpublish blogs');
      return;
    }
    
    try {
      const blogRef = doc(db, 'blogs', blog.id);
      await updateDoc(blogRef, {
        isPublished: !blog.isPublished
      });
      // No need to update state manually as the query will re-run
    } catch (err) {
      setError('Error updating blog: ' + err.message);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Blog Posts</h2>
        <Link to="/admin/blogs/new" className="btn btn-primary">
          Create New Blog
        </Link>
      </div>
      
      {combinedError && <ErrorDisplay message={combinedError} />}
      
      {blogs.length === 0 ? (
        <ErrorDisplay
          message="No blog posts found. Create your first blog post!"
          type="info"
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Image</th>
                <th>Event Date</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map(blog => (
                <tr key={blog.id}>
                  <td>{blog.title}</td>
                  <td>
                    <span className={`badge ${blog.isPublished ? 'bg-success' : 'bg-secondary'}`}>
                      {blog.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>
                    {blog.imageUrl ? (
                      <img
                        src={blog.imageUrl}
                        alt="Blog thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                        className="img-thumbnail"
                      />
                    ) : (
                      <span className="text-muted">No image</span>
                    )}
                  </td>
                  <td>
                    {blog.eventDate ? (
                      <div>
                        <i className="bi bi-calendar-event me-1"></i> {formatShortDateTime(blog.eventDate)}
                        {blog.eventEndDate && (
                          <div className="small text-muted">
                            <i className="bi bi-clock me-1"></i> End: {formatShortDateTime(blog.eventEndDate)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">No event</span>
                    )}
                  </td>
                  <td>
                    {formatShortDate(blog.createdAt)}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <Link to={`/admin/blogs/edit/${blog.id}`} className="btn btn-outline-primary">
                        Edit
                      </Link>
                      <button
                        className={`btn btn-outline-${blog.isPublished ? 'warning' : 'success'}`}
                        onClick={() => handleTogglePublish(blog)}
                      >
                        {blog.isPublished ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDelete(blog.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogList;